'use client'
import { Flex, HStack, StackDivider, VStack, Text } from '@chakra-ui/react'
import { GrandImage, GrandText } from 'ui'
import { PaymentMethodLogos } from 'storefront-modules/layout/PaymentMethodLogos'
import { useAppConfigContext } from '#/src/appConfig'
import klarnaLogo from '#/src/images/Klarna_Logo.png'
import nexiKlarnaLogoImg from '#/src/images/nexi_klarna_logo.webp'
import qliroLogoImg from '#/src/images/qliro_logo.png'
import sveaBannerImg from '#/src/images/svea_banner.jpg'
import { Currency, Language } from 'ecosystem'
import { useOverridesContext } from 'ui/lib/overrides/hooks'
import { CurrencyOverrides } from 'storefront-modules/types'
import { OverridesContextProvider, OverridesContextType, overrideText } from 'ui/lib/overrides'

type CustomProductDeliveryOverrides = CurrencyOverrides & {
  payAfterDelivery?: string
  freeShippingOver?: string
  guarantee?: string
  installation?: string
}

const customProductDeliveryOverridesLabels: Record<Language, CustomProductDeliveryOverrides> = {
  en: {
    payAfterDelivery: 'Pay after delivery',
    freeShippingOver: 'free shipping over',
    guarantee: 'Guarantee',
    installation: 'Installation'
  },
  no: {
    payAfterDelivery: 'Betal etter levering',
    freeShippingOver: 'gratis frakt over',
    guarantee: 'Garanti',
    installation: 'Installasjon'
  },
  de: {
    payAfterDelivery: 'Zahlen nach Lieferung',
    freeShippingOver: 'kostenloser Versand ab',
    guarantee: 'Garantie',
    installation: 'Installation'
  },
  sv: {
    payAfterDelivery: 'Betala efter leverans',
    freeShippingOver: 'fri frakt över',
    guarantee: 'Garanti',
    installation: 'Installation'
  },
  da: {
    payAfterDelivery: 'Betal efter levering',
    freeShippingOver: 'gratis fragt over',
    guarantee: 'Garanti',
    installation: 'Installation'
  },
  fi: {
    payAfterDelivery: 'Maksa toimituksen jälkeen',
    freeShippingOver: 'ilmainen toimitus yli',
    guarantee: 'Takuu',
    installation: 'Asennus'
  }
}

const CustomProductDelivery = () => {
  const appConfig = useAppConfigContext()
  const guarantyProps = appConfig.theme?.components?.ProductDeliveryGuaranty ?? {}
  const installationProps = appConfig.theme?.components?.ProductDeliveryInstallation ?? {}
  const freeDeliveryProps = appConfig.theme?.components?.ProductDeliveryFreeDelivery ?? {}
  const overrides = useOverridesContext<keyof CustomProductDeliveryOverrides>()
  const PaymentProviderInfo = () => {
    // Merchant-curated method marks win over the per-provider badge below, which stays
    // as the fallback for stores that have not configured any.
    const methodLogos = appConfig.paymentMethodLogos

    if (
      methodLogos?.productPage?.methods?.length ||
      methodLogos?.productPage?.combinedImageUrl ||
      methodLogos?.productPage?.combinedPreset
    ) {
      return <PaymentMethodLogos config={methodLogos.productPage} />
    }

    switch (appConfig.paymentProviderConfig?.providerName ?? 'KLARNA') {
      case 'KLARNA':
        return (
          <VStack alignItems="flex-start" flex={1} p={2}>
            <GrandText>
              {overrideText('Betala efter leverans', overrides?.payAfterDelivery)}
            </GrandText>
            <Flex justifyContent="center">
              <GrandImage alt="Klarna" src={klarnaLogo} title="klarna" maxW="150px" />
            </Flex>
          </VStack>
        )
      case 'NEXI':
        return (
          <VStack alignItems="flex-start" flex={1} p={2}>
            <GrandImage alt="Nexi payment methods" src={nexiKlarnaLogoImg} maxW="200px" />
          </VStack>
        )
      case 'SVEA':
        return (
          <VStack alignItems="flex-start" flex={1} p={2}>
            <GrandImage alt="Svea payment" src={sveaBannerImg} maxW="250px" />
          </VStack>
        )
      // Briqpay is an orchestrator — which methods a shopper sees comes from the Briqpay
      // portal, so there is no single logo to show here.
      case 'BRIQPAY':
        return null
      // todo: add LEDYER info
      case 'LEDYER':
        return <>LEDYER info</>
      case 'QLIRO':
        return (
          <VStack alignItems="flex-start" flex={1} p={2}>
            <GrandImage alt="Qliro" src={qliroLogoImg} maxW="200px" />
          </VStack>
        )
    }
  }

  return (
    <VStack>
      <HStack
        justify="center"
        gap={8}
        w="100%"
        sx={{
          '& > *:not(:first-child):before': {
            content: '""',
            display: 'block',
            height: '100%',
            width: '1px',
            background: 'var(--chakra-colors-chakra-border-color)',
            position: 'absolute',
            left: -4,
            top: 0
          }
        }}
      >
        <GrandText align="center" fontSize="sm" position="relative" {...freeDeliveryProps}>
          {`${overrideText('fri frakt över', overrides?.freeShippingOver)} `}

          <Text as="span" whiteSpace="nowrap">
            {`${appConfig.freeShippingOver} ${overrideText(appConfig.currency ?? Currency.SEK, overrides?.currency)} `}
          </Text>
        </GrandText>

        <GrandText align="center" fontSize="sm" position="relative" {...guarantyProps}>
          {overrideText('Garanti', overrides?.guarantee)}
        </GrandText>

        <GrandText
          align="center"
          bg="#dff7df"
          borderRadius="sm"
          fontSize="sm"
          p={1}
          position="relative"
          {...installationProps}
        >
          {overrideText('Installation', overrides?.installation)}
        </GrandText>
      </HStack>

      <HStack divider={<StackDivider />}>
        <PaymentProviderInfo />
      </HStack>
    </VStack>
  )
}

const CustomProductDeliveryWithTranslation = () => {
  const appConfig = useAppConfigContext()
  return (
    <OverridesContextProvider
      overrides={
        (customProductDeliveryOverridesLabels[appConfig.language] as OverridesContextType) ?? {}
      }
    >
      <CustomProductDelivery />
    </OverridesContextProvider>
  )
}

export default CustomProductDeliveryWithTranslation
